import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useUser } from '../context/UserContext';
import './hostDashboard.css';

const currency = (value) => `$${(Number(value) || 0).toFixed(2)}`;

const formatStatus = (value) => {
    const normalized = String(value || 'unknown').replace(/_/g, ' ');
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const normalized = String(dateStr).slice(0, 10);
    const [year, month, day] = normalized.split('-').map(Number);
    const date = new Date(year, (month || 1) - 1, day || 1);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const toLocalDateKey = (value = new Date()) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const HostDashboard = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [payoutFilter, setPayoutFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [cancellationMessage, setCancellationMessage] = useState({ type: '', text: '' });
    const [cancellingReservationId, setCancellingReservationId] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [selectedReservation, setSelectedReservation] = useState(null);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        const loadReservations = async () => {
            try {
                setLoading(true);
                const response = await api.get('/direct-booking/host/reservations');
                setReservations(response.data || []);
            } catch (e) {
                console.error('Failed to load reservations', e);
                setCancellationMessage({
                    type: 'error',
                    text: 'Failed to load reservations'
                });
            } finally {
                setLoading(false);
            }
        };

        loadReservations();
    }, [user, navigate]);

    const handleCancelClick = (reservation) => {
        setSelectedReservation(reservation);
        setCancelReason('');
        setShowCancelModal(true);
    };

    const submitCancellation = async () => {
        if (!selectedReservation) return;

        try {
            setCancellingReservationId(selectedReservation.id);
            setCancellationMessage({ type: '', text: '' });

            const response = await api.post(
                `/direct-booking/host/trips/${selectedReservation.id}/cancel`,
                { reason: cancelReason }
            );

            setCancellationMessage({
                type: 'success',
                text: `Reservation cancelled. Refund of ${currency(response.data.refundedAmount)} initiated.`
            });

            setReservations((prev) =>
                prev.map((r) =>
                    r.id === selectedReservation.id
                        ? {
                            ...r,
                            status: 'cancelled',
                            refundedAmount: response.data.refundedAmount,
                            payoutStatus: r.payoutStatus === 'submitted' ? 'submitted' : r.payoutStatus,
                            cancellationReason: cancelReason || 'Cancelled by host'
                        }
                        : r
                )
            );

            setShowCancelModal(false);
            setSelectedReservation(null);
        } catch (e) {
            const backendError = e?.response?.data?.error;
            setCancellationMessage({
                type: 'error',
                text: backendError || 'Failed to cancel reservation'
            });
        } finally {
            setCancellingReservationId(null);
        }
    };

    const today = toLocalDateKey();
    const upcomingCount = reservations.filter((r) => r.startDate > today && r.status !== 'cancelled').length;
    const totalRevenue = reservations
        .filter((r) => r.status !== 'cancelled')
        .reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0);
    const refundedTotal = reservations.reduce((sum, r) => sum + (Number(r.refundedAmount) || 0), 0);
    const payoutSubmittedCount = reservations.filter((r) => String(r.payoutStatus || '').toLowerCase() === 'submitted').length;

    const filteredReservations = reservations.filter((reservation) => {
        const matchesStatus = statusFilter === 'all'
            ? true
            : String(reservation.status || '').toLowerCase() === statusFilter.toLowerCase();

        const matchesPayout = payoutFilter === 'all'
            ? true
            : String(reservation.payoutStatus || '').toLowerCase() === payoutFilter.toLowerCase();

        const query = String(searchTerm || '').trim().toLowerCase();
        const matchesSearch = !query || [
            reservation.guestName,
            reservation.guestEmail,
            reservation.property?.name,
            reservation.listing?.title,
            reservation.listing?.slug,
            reservation.cancellationReason
        ].some((value) => String(value || '').toLowerCase().includes(query));

        return matchesStatus && matchesPayout && matchesSearch;
    });

    if (!user) {
        return null;
    }

    if (loading) {
        return (
            <div className="direct-booking-page">
                <p>Loading your reservations...</p>
            </div>
        );
    }

    return (
        <div className="direct-booking-page host-dashboard-page">
            <h1>Direct Booking Dashboard</h1>
            <p className="host-dashboard-subtitle">Manage your direct booking reservations</p>

            {cancellationMessage.text && (
                <div className={`direct-booking-alert ${cancellationMessage.type === 'success' ? 'direct-booking-alert-success' : 'direct-booking-alert-error'}`}>
                    {cancellationMessage.text}
                </div>
            )}

            <div className="host-dashboard-stats">
                <div className="host-dashboard-stat-card">
                    <div className="stat-label">Upcoming Reservations</div>
                    <div className="stat-value">{upcomingCount}</div>
                </div>
                <div className="host-dashboard-stat-card">
                    <div className="stat-label">Total Revenue</div>
                    <div className="stat-value">{currency(totalRevenue)}</div>
                </div>
                <div className="host-dashboard-stat-card">
                    <div className="stat-label">Total Reservations</div>
                    <div className="stat-value">{reservations.length}</div>
                </div>
                <div className="host-dashboard-stat-card">
                    <div className="stat-label">Refunded Total</div>
                    <div className="stat-value">{currency(refundedTotal)}</div>
                </div>
                <div className="host-dashboard-stat-card">
                    <div className="stat-label">Submitted Payouts</div>
                    <div className="stat-value">{payoutSubmittedCount}</div>
                </div>
            </div>

            <div className="host-dashboard-filters">
                <label>Search:</label>
                <input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="host-dashboard-filter-input"
                    placeholder="Guest, listing, property, reason"
                />
                <label>Status:</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="host-dashboard-filter-select">
                    <option value="all">All Reservations</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="cancelled">Cancelled</option>
                </select>
                <label>Payout:</label>
                <select value={payoutFilter} onChange={(e) => setPayoutFilter(e.target.value)} className="host-dashboard-filter-select">
                    <option value="all">All Payouts</option>
                    <option value="submitted">Submitted</option>
                    <option value="failed">Failed</option>
                    <option value="not_connected">Not Connected</option>
                    <option value="not_applicable">Not Applicable</option>
                </select>
            </div>

            <div className="host-dashboard-table-wrapper">
                {filteredReservations.length === 0 ? (
                    <div className="host-dashboard-empty">
                        <p>No reservations found.</p>
                    </div>
                ) : (
                    <table className="host-dashboard-table">
                        <thead>
                            <tr>
                                <th>Guest</th>
                                <th>Stay</th>
                                <th>Listing</th>
                                <th>Amounts</th>
                                <th>Reservation</th>
                                <th>Payout</th>
                                <th>Chat</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredReservations.map((reservation) => {
                                const canCancel = !!reservation.canCancel;

                                return (
                                    <tr key={reservation.id} className={`status-${String(reservation.status || '').toLowerCase()}`}>
                                        <td>
                                            <strong>{reservation.guestName}</strong>
                                            <div className="host-dashboard-subtext">{reservation.guestEmail}</div>
                                            {reservation.guestPhone && <div className="host-dashboard-subtext">{reservation.guestPhone}</div>}
                                        </td>
                                        <td>
                                            <strong>{formatDate(reservation.startDate)}</strong>
                                            <div className="host-dashboard-subtext">to {formatDate(reservation.endDate)}</div>
                                            <div className="host-dashboard-subtext">{reservation.nights} night{reservation.nights !== 1 ? 's' : ''}</div>
                                        </td>
                                        <td>
                                            <strong>{reservation.listing?.title || 'Listing'}</strong>
                                            <div className="host-dashboard-subtext">{reservation.property?.name || 'Property'}</div>
                                            {reservation.listing?.slug && <div className="host-dashboard-subtext">/{reservation.listing.slug}</div>}
                                        </td>
                                        <td>
                                            <strong>{currency(reservation.totalAmount)}</strong>
                                            {reservation.refundedAmount > 0 && (
                                                <div className="refund-info">Refunded {currency(reservation.refundedAmount)}</div>
                                            )}
                                            {Number(reservation.payoutAmount) > 0 && (
                                                <div className="payout-info">Host payout {currency(reservation.payoutAmount)}</div>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`status-badge status-${String(reservation.status || '').toLowerCase()}`}>
                                                {formatStatus(reservation.status)}
                                            </span>
                                            <div className="host-dashboard-subtext">Payment {formatStatus(reservation.paymentStatus || 'unknown')}</div>
                                            {reservation.cancellationReason && <div className="host-dashboard-subtext">{reservation.cancellationReason}</div>}
                                        </td>
                                        <td>
                                            <span className={`status-badge payout-${String(reservation.payoutStatus || '').toLowerCase()}`}>
                                                {formatStatus(reservation.payoutStatus || 'unknown')}
                                            </span>
                                            {reservation.payoutTransferId && <div className="host-dashboard-subtext">Transfer {reservation.payoutTransferId}</div>}
                                            {reservation.payoutFailureReason && <div className="host-dashboard-subtext host-dashboard-error-copy">{reservation.payoutFailureReason}</div>}
                                        </td>
                                        <td>
                                            {reservation.conversationId ? (
                                                <button
                                                    type="button"
                                                    className="btn-secondary-sm"
                                                    onClick={() => navigate(`/Chat?conversationId=${reservation.conversationId}`)}
                                                >
                                                    Open Chat
                                                </button>
                                            ) : <span className="action-placeholder">—</span>}
                                        </td>
                                        <td>
                                            {canCancel && (
                                                <button
                                                    type="button"
                                                    className="btn-danger-sm"
                                                    onClick={() => handleCancelClick(reservation)}
                                                    disabled={cancellingReservationId === reservation.id}
                                                >
                                                    {cancellingReservationId === reservation.id ? 'Cancelling...' : 'Cancel'}
                                                </button>
                                            )}
                                            {!canCancel && <span className="action-placeholder">—</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {showCancelModal && selectedReservation && (
                <div className="host-dashboard-modal-overlay" onClick={() => setShowCancelModal(false)}>
                    <div className="host-dashboard-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="host-dashboard-modal-header">
                            <h3>Cancel Reservation</h3>
                            <button type="button" className="host-dashboard-modal-close" onClick={() => setShowCancelModal(false)}>&times;</button>
                        </div>
                        <div className="host-dashboard-modal-body">
                            <p className="modal-info">
                                Guest: <strong>{selectedReservation.guestName}</strong><br />
                                Dates: <strong>{formatDate(selectedReservation.startDate)} to {formatDate(selectedReservation.endDate)}</strong><br />
                                Amount: <strong>{currency(selectedReservation.totalAmount)}</strong><br />
                                Listing: <strong>{selectedReservation.listing?.title || 'Listing'}</strong>
                            </p>

                            <label htmlFor="cancelReason" className="modal-label">Cancellation Reason (optional):</label>
                            <textarea
                                id="cancelReason"
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="Enter reason for cancellation (to share with guest)"
                                className="modal-textarea"
                                rows="4"
                            />

                            <p className="modal-warning">
                                ⚠️ This will refund <strong>{currency(selectedReservation.totalAmount)}</strong> to the guest and cancel the booking.
                            </p>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => setShowCancelModal(false)}
                                >
                                    Keep Reservation
                                </button>
                                <button
                                    type="button"
                                    className="btn-danger"
                                    onClick={submitCancellation}
                                    disabled={cancellingReservationId === selectedReservation.id}
                                >
                                    {cancellingReservationId === selectedReservation.id ? 'Processing...' : 'Confirm Cancellation & Refund'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HostDashboard;
